import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SheffieldCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sheffield Pothole Claims | Streets Ahead PFI & 12,000 Repairs | Fixtyer"
        description="Sheffield earns a GREEN condition scorecard yet AMBER overall — with no DfT spend rating because of the Streets Ahead PFI. U-road RED condition climbed from 8% to 12%, and 11,949 potholes were filled in 2024/25 alone. See what this means for your claim."
        canonical="/councils/sheffield"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Sheffield', url: '/councils/sheffield' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Sheffield has a GREEN condition rating — can I still claim?',
            answer: 'Yes. The DfT Condition scorecard is GREEN, but your claim turns on the specific defect that damaged your vehicle, not the network average. Sheffield\'s own data shows U-road RED condition climbing from 8% to 12% since 2020, and Category 1 pothole repairs more than doubled in 2024/25. Section 58 depends on whether your road was reasonably inspected and the defect repaired in time — not on headline scorecards.',
          },
          {
            question: 'Why is there no DfT Spend rating for Sheffield?',
            answer: 'Sheffield\'s highways are maintained under the 25-year Streets Ahead PFI contract with Amey Hallam Highways Limited. The council pays a single aggregated monthly fee covering street lighting, cleaning, resurfacing and pothole repairs — and states it "do[es] not have a figure for the costs incurred by Amey for individual operations." Cost information is considered commercially sensitive. The DfT cannot score spend against allocation when no comparable figures are published.',
          },
          {
            question: 'What if my pothole was on a side road or unclassified road?',
            answer: 'Unclassified roads make up 1,402.7km — 73% of Sheffield\'s 1,922km road network. The council inspects "local roads, for example, side roads" annually, while A and B roads are inspected monthly. Condition surveys run on a two-year cycle, so only half the network is fully assessed each year. U-road RED condition has risen from 8% in 2020 to 12% in 2024 — still below England\'s 17% average for unclassified roads, but trending the wrong way.',
          },
          {
            question: 'Sheffield repaired nearly 12,000 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The council filled 6,179 Category 1 and 5,770 Category 2 potholes in 2024/25 — 11,949 in total, up sharply from prior years. That volume is evidence of a network still producing defects at scale. The council also notes it may leave less urgent potholes unrepaired while prioritising hazardous ones, and that a single repair can cover multiple defects.',
          },
          {
            question: 'Does the Streets Ahead PFI contract help or hurt my claim?',
            answer: 'Both, depending on your road. The PFI imposes output specifications including condition scores and pothole response times — a documented maintenance system the council can cite under Section 58. But the council admits Amey ran a fully reactive strategy for the first 12 years of the contract (2020/21 through 2023/24: 0% preventative, 100% reactive). Preventative maintenance only began in 2024/25 at 24.3%. A reactive-first history weakens the "reasonable system" argument for defects that formed between inspections.',
          },
          {
            question: 'Side roads are only inspected annually — what does that mean for Section 58?',
            answer: 'Annual inspection of local roads means a pothole can form and persist for up to 12 months before a scheduled safety inspection discovers it — unless a member of the public reports it. The council accepts reports via FixMyStreet, its website and phone. Prior reports of the same defect are therefore critical: they establish actual notice before your incident, bypassing the annual inspection gap.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Sheffield City Council"
        councilSlug="sheffield"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/sheffield"
        headline="Sheffield Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Sheffield</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-brand-200 text-sm font-medium">Not rated</span> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Sheffield: Green Condition, Amber Overall, No Spend Data
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Sheffield outperforms national road condition averages and earns a <strong className="text-emerald-400">GREEN</strong> DfT
            condition scorecard. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — and the DfT publishes
            <strong className="text-amber-400"> no spend rating at all</strong>, because the famous Streets Ahead PFI bundles every
            highways cost into a single confidential payment. Meanwhile U-road RED condition has climbed from 8% to 12%, and the
            council filled <strong className="text-rose-400">11,949 potholes in 2024/25 alone</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">12%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 8% in 2020 — a 50% rise on residential streets that make up 73% of
              Sheffield's 1,922km road network, inspected only once a year.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Streets Ahead PFI Context */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why There Is No Spend Rating
          </h2>
          <p className="text-surface-600 mb-8">
            Sheffield's highways maintenance runs through one of England's best-known PFI contracts — and the
            transparency report explains exactly why DfT spend data is absent
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Sheffield City Council has a PFI contract with Amey Hallam Highways Ltd for the delivery of a complete highway maintenance service which includes all highway maintenance activities such as street lighting, street cleaning, carriageway and footway resurfacing, signal maintenance etc. Payments made by the Council under the contract are made as a single aggregated payment for each month of the 25-year term of the contract. As a result, we do not have a figure for the costs incurred by Amey for individual operations."
              source="Sheffield City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The cost information from Amey LG is considered to be commercially sensitive and has been kept confidential to not Prejudice any commercial interests."
              source="Sheffield City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Streets Ahead Contract</h3>
              <p className="text-surface-600 text-sm mb-3">
                In 2012, Sheffield awarded Amey Hallam Highways Limited the 25-year Streets Ahead Highways
                Maintenance PFI. Amey LG subcontracts delivery of rehabilitation, maintenance and operation
                across roads, footways and cycleways.
              </p>
              <p className="text-surface-600 text-sm">
                The council funds this through an annual fee from government grants and council budgets — but
                cannot break out pothole repair costs, resurfacing spend or DfT allocation comparisons.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">12 Years of Pure Reactive Maintenance</h3>
              <p className="text-amber-800 text-sm mb-3">
                From contract commencement through 2023/24, Amey adopted a reactive maintenance strategy —
                monitoring the network for non-compliance and intervening only when standards were breached.
              </p>
              <p className="text-amber-800 text-sm">
                Preventative maintenance only began in 2024/25. For five published years, the preventative
                share was <strong>0%</strong> and reactive was <strong>100%</strong>.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Preventative vs Reactive: The Shift</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">0%</div>
                <div className="text-brand-300 text-sm">Preventative share 2020/21–2023/24</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">24.3%</div>
                <div className="text-brand-300 text-sm">Preventative share 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">23%</div>
                <div className="text-brand-300 text-sm">Forecast preventative 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              A network run reactively for a dozen years produces potholes by design — repairs happen
              after failure, not before. The shift to preventative maintenance is recent and still leaves
              <span className="text-amber-400"> 77% reactive forecast for 2025/26</span>.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Sheffield's own transparency report — strong headline scores,
            but U-road RED trending the wrong way
          </p>

          {/* Network size */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Network size (km)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">201.2</div>
                <div className="text-sm text-surface-600">A roads (10.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">318.1</div>
                <div className="text-sm text-surface-600">B and C roads (16.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-rose-600">1,402.7</div>
                <div className="text-sm text-surface-600">U roads (73.0%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">1,922</div>
                <div className="text-sm text-surface-600">Total roads</div>
              </div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (201.2km): nationally outperforming</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.2%</td>
                    <td className="px-4 py-2 text-center">10.1%</td>
                    <td className="px-4 py-2 text-center">88.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">0.9%</td>
                    <td className="px-4 py-2 text-center">9.7%</td>
                    <td className="px-4 py-2 text-center">89.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.1%</td>
                    <td className="px-4 py-2 text-center">9.7%</td>
                    <td className="px-4 py-2 text-center">89.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">11.6%</td>
                    <td className="px-4 py-2 text-center">86.8%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.0%</td>
                    <td className="px-4 py-2 text-center font-bold">10.9%</td>
                    <td className="px-4 py-2 text-center font-bold">88.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              England's average is approximately 4% of A-roads requiring maintenance. Sheffield's latest
              figure is 1% — genuinely strong on main routes.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (318.1km): stable at 3% RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">23.3%</td>
                    <td className="px-4 py-2 text-center">74.0%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.0%</td>
                    <td className="px-4 py-2 text-center">20.1%</td>
                    <td className="px-4 py-2 text-center">77.9%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">17.6%</td>
                    <td className="px-4 py-2 text-center">80.7%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">14.0%</td>
                    <td className="px-4 py-2 text-center">83.0%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3.0%</td>
                    <td className="px-4 py-2 text-center font-bold">14.0%</td>
                    <td className="px-4 py-2 text-center font-bold">83.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              Against England's average of 7% of B and C roads requiring maintenance, Sheffield's 3% is
              well below the national benchmark — but 17% of the B/C network is still in Amber condition.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (1,402.7km — 73% of network): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">8%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">10%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">10%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED condition has climbed 50% since 2020 (8% → 12%). England's average for unclassified
              roads is 17% — Sheffield still beats that — but the trend on residential streets is worsening,
              not improving. At 12%, roughly 168km of U-roads need maintenance.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Two-Year Survey Cycle</h3>
            <p className="text-brand-200 mb-4">
              Sheffield's own report states condition surveys run across all adopted highways over a
              two-year cycle — half the network assessed each year. Combined with annual safety inspections
              on side roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the surveyed half the year your pothole formed?</li>
              <li>• If U-road RED has risen to 12%, what was done about your street specifically?</li>
              <li>• Does a GREEN headline score reflect the road you were actually driving on?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN condition scorecard built on network averages does not mean your specific
              residential road was in acceptable condition.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            32,851 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Category 1 and Category 2 repairs from Sheffield's own transparency report — with a sharp spike in 2024/25
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">CAT1 (hazardous)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">CAT2 (lower category)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,348</td>
                  <td className="px-6 py-4 text-center">2,448</td>
                  <td className="px-6 py-4 text-center font-bold">4,796</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,707</td>
                  <td className="px-6 py-4 text-center">1,539</td>
                  <td className="px-6 py-4 text-center font-bold">4,246</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,862</td>
                  <td className="px-6 py-4 text-center">1,650</td>
                  <td className="px-6 py-4 text-center font-bold">4,512</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,762</td>
                  <td className="px-6 py-4 text-center">1,586</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,348</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,179</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,770</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,949</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">19,858</td>
                  <td className="px-6 py-4 text-center font-bold">12,993</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">32,851</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~18 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Sheffield repairs around 18 potholes per day. In 2024/25 alone,
                that rose to roughly 33 per day. A network producing defects at that rate is one where
                potholes routinely form between inspections — exactly the scenario where prior reports and
                photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forecast: 7,800 More</h3>
              <p className="text-amber-800 text-sm">
                The council estimates approximately 7,800 pothole repairs during 2025/26. Pothole and
                other defect repairs have amounted to 12% on average over the last five years of reactive
                maintenance investment — a slice of a much larger PFI payment the public cannot see.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Prioritising the most hazardous potholes means that we may leave other less urgent potholes, even if they are close by. In this case, we check if the potholes are on a route that we already have planned for resurfacing. We then look at starting work on those roads earlier than planned and design appropriate diversion routes to ensure minimal disruption to road users."
              source="Sheffield City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Inspection Regime */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Inspection Gap on Side Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Sheffield's own inspection frequencies — and what they mean for defects on residential streets
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road type</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Safety inspection frequency</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Primary (A) and secondary (B) roads</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-700">Monthly</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Link roads</td>
                  <td className="px-6 py-4 text-center font-medium text-amber-700">Quarterly</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Local roads — side roads</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">Annually</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Response Times</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• CAT1 hazardous potholes (&gt;40mm): temporary fill within 24 hours of report</li>
                <li>• Permanent inlay patch: within 28 days of marking</li>
                <li>• Reports accepted via FixMyStreet, council website, email and phone</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Annual Inspection Problem</h3>
              <p className="text-amber-800 text-sm">
                On side roads — 73% of the network — a defect can exist for months before the next
                scheduled safety inspection. If nobody reported it, the council may argue it had no
                notice. Your prior FixMyStreet reports become the difference between no-notice and
                actual knowledge.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Sheffield must maintain its roads. Under
              Section 58, it can defend a claim by showing it took reasonable care — but that defence
              requires evidence of a reasonable inspection and repair system:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road a side road with only annual inspections?</li>
              <li>• Did you or anyone else report the defect before your incident?</li>
              <li>• If CAT1 repairs doubled in 2024/25, was your pothole below the 40mm hazardous threshold?</li>
              <li>• Did the council leave your defect as "less urgent" while fixing a neighbouring one?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits leaving less urgent potholes unrepaired cannot claim every defect
              on every road was known and managed.
            </p>
          </div>
        </div>
      </section>

      {/* Forward Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26: The Preventative Turn
          </h2>
          <p className="text-surface-600 mb-8">
            Sheffield's forward plan — resurfacing miles, structure repairs and the shift away from pure reactive maintenance
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="From the commencement of the contract through to the 2023/2024 period (the first 12 years), Amey adopted a reactive maintenance strategy. This involved continuous monitoring of the highway network to identify non-compliance with performance standards and implementing targeted interventions accordingly. Beginning in 2024/2025, Amey transitioned to a preventative maintenance model, emphasising early intervention strategies aimed at extending the lifespan of the existing road pavement infrastructure."
              source="Sheffield City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">62 mi</div>
              <div className="text-sm text-surface-600">Carriageway resurfacing planned</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">48 mi</div>
              <div className="text-sm text-surface-600">Surface dressing (preventative)</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">38 mi</div>
              <div className="text-sm text-surface-600">Footway resurfacing planned</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">906</div>
              <div className="text-sm text-surface-600">Footway sections to inspect and repair</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2024/25 Delivered</h3>
              <p className="text-surface-600 text-sm">
                Between April 2024 and March 2025, 33.16 miles of carriageway and 41.07 miles of
                footways were resurfaced. Four major structures are planned for repair in 2025/26 at
                Handsworth Road, Sugworth Road, Ecclesall Road South and A57 Manchester Road.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What the Plan Promises</h3>
              <p className="text-emerald-800 text-sm">
                The preventative carriageway asset management strategy targets early intervention
                through micro-asphalts and surface dressings — but 2025/26 still forecasts 77% reactive.
                Plans are not proof that your specific road was maintained before your incident.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Claims balance */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Condition AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Sheffield is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — A, B/C and U-road RED all below national averages
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> PFI output specifications with defined condition thresholds and pothole response times
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Monthly inspections on A and B roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented 24-hour emergency and 28-day permanent repair targets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> FixMyStreet and council reporting channels establish a notice trail
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on main roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and AMBER best practice — not a model authority
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No published spend data — impossible to verify investment against DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED up 50% since 2020 (8% → 12%) on 73% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Side roads inspected only annually — months of potential blind spots
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12 years of 100% reactive maintenance before preventative work began
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits leaving less urgent potholes unrepaired
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 32,851 pothole repairs in five years — defects still forming at scale
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN condition scorecard and a formal PFI inspection regime, your
              claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a side road, the annual inspection gap is your strongest structural argument</li>
              <li>• Whether the pothole was below the 40mm CAT1 threshold the council uses to prioritise repairs</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Sheffield's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Sheffield?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-documented council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road trend argument</li>
                <li>• No prior-report search</li>
                <li>• No annual inspection-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise documented (8% → 12%)</li>
                <li>✅ Annual side-road inspection gap argued</li>
                <li>✅ 32,851 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Sheffield</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=sheffield"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Sheffield Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sheffield has a GREEN condition rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition scorecard is GREEN, but your claim turns on the specific defect
                that damaged your vehicle, not the network average. Sheffield's own data shows U-road RED
                condition climbing from 8% to 12% since 2020, and Category 1 pothole repairs more than
                doubled in 2024/25. Section 58 depends on whether your road was reasonably inspected and
                the defect repaired in time — not on headline scorecards.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is there no DfT Spend rating for Sheffield?
              </h3>
              <p className="text-surface-600 text-sm">
                Sheffield's highways are maintained under the 25-year Streets Ahead PFI contract with Amey
                Hallam Highways Limited. The council pays a single aggregated monthly fee covering street
                lighting, cleaning, resurfacing and pothole repairs — and states it "do[es] not have a figure
                for the costs incurred by Amey for individual operations." Cost information is considered
                commercially sensitive. The DfT cannot score spend against allocation when no comparable
                figures are published.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a side road or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,402.7km — 73% of Sheffield's 1,922km road network. The council
                inspects "local roads, for example, side roads" annually, while A and B roads are inspected
                monthly. Condition surveys run on a two-year cycle, so only half the network is fully assessed
                each year. U-road RED condition has risen from 8% in 2020 to 12% in 2024 — still below
                England's 17% average for unclassified roads, but trending the wrong way.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sheffield repaired nearly 12,000 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council filled 6,179 Category 1 and 5,770 Category 2 potholes in 2024/25 — 11,949
                in total, up sharply from prior years. That volume is evidence of a network still producing
                defects at scale. The council also notes it may leave less urgent potholes unrepaired while
                prioritising hazardous ones, and that a single repair can cover multiple defects.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the Streets Ahead PFI contract help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Both, depending on your road. The PFI imposes output specifications including condition
                scores and pothole response times — a documented maintenance system the council can cite
                under Section 58. But the council admits Amey ran a fully reactive strategy for the first
                12 years of the contract (2020/21 through 2023/24: 0% preventative, 100% reactive).
                Preventative maintenance only began in 2024/25 at 24.3%. A reactive-first history weakens
                the "reasonable system" argument for defects that formed between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Side roads are only inspected annually — what does that mean for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Annual inspection of local roads means a pothole can form and persist for up to 12 months
                before a scheduled safety inspection discovers it — unless a member of the public reports it.
                The council accepts reports via FixMyStreet, its website and phone. Prior reports of the
                same defect are therefore critical: they establish actual notice before your incident,
                bypassing the annual inspection gap.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.sheffield.gov.uk/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Sheffield City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
